/**
 * useCliVersionNudge — informational MIN_CLI_VERSION toast.
 *
 * Mirrors the desktop force-update gate (`MIN_DESKTOP_VERSION`) but
 * non-blocking. Fires once per workspace mount when the locally
 * installed `ano` CLI is below the server-published minimum, as a
 * sticky Sonner toast (`duration: Infinity`) with an "Upgrade" action
 * that asks the in-shell agent to run the upgrade (via `queueAgentTask`,
 * the onboarding-style launch-prompt / paste path — see #1898). Dismissed via X →
 * remembered in localStorage keyed by the violation's `minVersion`,
 * so a fresh server bump (2.7.0 → 2.8.0) re-shows the toast even if
 * the previous one was dismissed.
 *
 * Web runtime: no-op (CLI doesn't exist there).
 * Electron + CLI not installed: fires when `MIN_CLI_VERSION > 0.0.0`.
 * Electron + CLI installed and current: no-op.
 *
 * Replaces the previous in-page banner — toasts are app-wide so the
 * nudge isn't gated on the user landing on the Automations page, and
 * the visual style is consistent with the rest of the app's
 * notification UX.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";

import { isElectronRuntime } from "@/lib/logout";
import { API_URL } from "@/lib/config";
import { checkMinCliVersion } from "@/lib/min-cli-version-check";
import { queueAgentTask } from "@/lib/pending-agent-task";

declare global {
  interface Window {
    __electronApi?: {
      // Mirrored in useAnoMcpNudge.ts — TypeScript interface merging
      // requires both declarations to expose the same inner shape, so
      // keep them in lockstep when adding/changing API methods.
      anoCliGetVersion?: () => Promise<{
        version: string | null;
        error?: string;
      }>;
      anoSkillGetVersion?: () => Promise<{
        version: string | null;
        error?: string;
      }>;
      claudeCheckAnoMcp?: () => Promise<{
        status:
          | "connected"
          | "needs_auth"
          | "missing"
          | "claude_missing"
          | "unknown";
        url?: string;
        error?: string;
      }>;
    };
  }
}

const TOAST_ID = "cli-version-nudge";
const DISMISS_STORAGE_KEY = "ano-cli-version-nudge-dismissed";

function isDismissedFor(minVersion: string): boolean {
  try {
    return localStorage.getItem(DISMISS_STORAGE_KEY) === minVersion;
  } catch {
    return false;
  }
}

function rememberDismissal(minVersion: string): void {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, minVersion);
  } catch {
    /* ignore storage failures */
  }
}

/**
 * Build the instruction handed to the in-shell agent. We ask the agent to run
 * the command rather than typing it at the prompt ourselves: the embedded
 * Shell runs an AI agent, so a raw command would land in the agent's TUI or
 * race its launch (#1898). `queueAgentTask` delivers this via the agent's
 * launch prompt on a cold start, or by pasting to a running agent.
 */
function buildUpgradeInstruction(cmd: string, isMissing: boolean): string {
  const verb = isMissing ? "install" : "update";
  return (
    `Please ${verb} the Ano CLI for me. Run this command, then tell me ` +
    `whether it succeeded:\n\n${cmd}\n\nIf it fails with a permissions ` +
    `error, show me the command to run instead rather than running it yourself.`
  );
}

export function useCliVersionNudge(enabled: boolean): void {
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !isElectronRuntime() || firedRef.current) return;
    let cancelled = false;
    void (async () => {
      const probe = await window.__electronApi
        ?.anoCliGetVersion?.()
        .catch(() => null);
      const currentVersion = probe?.version ?? null;
      const v = await checkMinCliVersion({
        apiBaseUrl: API_URL,
        currentVersion,
      });
      if (cancelled || !v) return;
      if (isDismissedFor(v.minVersion)) return;
      firedRef.current = true;

      const isMissing = v.currentVersion === "(not installed)";
      toast.warning(
        isMissing ? "Install the Ano CLI" : "Your Ano CLI is out of date",
        {
          id: TOAST_ID,
          description: isMissing
            ? `Lets Claude Code work with channels, automations, tables, coworkers, and team admin from the Shell. Requires ${v.minVersion}+.`
            : `${v.currentVersion} → ${v.minVersion} adds the latest commands (channel admin, coworker setup, DND, notification preferences, invites).`,
          duration: Infinity,
          closeButton: true,
          action: {
            label: isMissing ? "Install" : "Upgrade",
            onClick: () =>
              queueAgentTask(
                buildUpgradeInstruction(v.upgradeCommand, isMissing),
              ),
          },
          onDismiss: () => rememberDismissal(v.minVersion),
        },
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);
}
